import React from 'react';
import { X, Info, Calculator, Target, MapPin, Mail, Flame } from 'lucide-react';

interface AboutModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AboutModal: React.FC<AboutModalProps> = ({
  isOpen,
  onClose,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center shadow-sm">
              <Flame className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">About FuelHunt</h2>
              <p className="text-sm text-gray-600">Find the best fuel prices across the UK</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* What is FuelHunt */}
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Target className="w-5 h-5 text-amber-500" />
              <h3 className="text-lg font-medium text-gray-900">What is FuelHunt?</h3>
            </div>
            <p className="text-gray-700 ml-7">
              FuelHunt helps you find the cheapest fuel stations near you across the UK. 
              We combine real-time fuel prices with your vehicle's running costs to show 
              you the total cost of refueling, including the cost of getting there.
            </p>
          </div>

          {/* How it works */}
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <MapPin className="w-5 h-5 text-amber-500" />
              <h3 className="text-lg font-medium text-gray-900">How it works</h3>
            </div>
            <div className="ml-7 space-y-2 text-gray-700">
              <p>1. <strong>Enter your location</strong> - Search for a UK postcode or city</p>
              <p>2. <strong>Configure your vehicle</strong> - Set fuel type and running cost per mile</p>
              <p>3. <strong>View recommendations</strong> - See stations ranked by total cost (fuel + travel)</p>
              <p>4. <strong>Save money</strong> - Choose the most cost-effective option for your needs</p>
            </div>
          </div>

          {/* Cost Calculations */}
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Calculator className="w-5 h-5 text-amber-500" />
              <h3 className="text-lg font-medium text-gray-900">How costs are calculated</h3>
            </div>
            <div className="ml-7 space-y-4">
              {/* Total Cost Formula */}
              <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
                <h4 className="font-medium text-amber-900 mb-2">Total Cost Formula:</h4>
                <div className="text-amber-800 space-y-1">
                  <div><strong>Fuel Cost:</strong> Fuel Amount (L) × Price per Liter</div>
                  <div><strong>Travel Cost:</strong> Distance × Vehicle Running Cost × Trip Type</div>
                  <div><strong>Total Cost:</strong> Fuel Cost + Travel Cost</div>
                </div>
              </div>

              {/* Trip Types */}
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Trip Types:</h4>
                <div className="text-gray-700 space-y-1">
                  <div><strong>Single Trip:</strong> Travel cost = Distance × Running cost</div>
                  <div><strong>Return Trip:</strong> Travel cost = Distance × 2 × Running cost</div>
                </div>
              </div>

              {/* Savings Calculation */}
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Savings Calculation:</h4>
                <div className="text-gray-700 space-y-1">
                  <div><strong>Baseline:</strong> Average fuel price per liter in your search area</div>
                  <div><strong>Savings:</strong> (Average Price - Station Price) × Fuel Amount</div>
                  <div><strong>Positive savings:</strong> Station costs less than area average</div>
                  <div><strong>Negative savings:</strong> Station costs more than area average</div>
                </div>
              </div>
            </div>
          </div>

          {/* Data Sources */}
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Info className="w-5 h-5 text-amber-500" />
              <h3 className="text-lg font-medium text-gray-900">Data Sources</h3>
            </div>
            <div className="ml-7 text-gray-700 space-y-2">
              <p>FuelHunt aggregates real-time fuel prices from 14+ major UK retailers including:</p>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div>• BP</div>
                <div>• Shell</div>
                <div>• Tesco</div>
                <div>• Asda</div>
                <div>• Sainsburys</div>
                <div>• Morrisons</div>
                <div>• Esso</div>
                <div>• Motor Fuel Group</div>
                <div>• And more...</div>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Prices are updated regularly throughout the day to ensure accuracy.
              </p>
            </div>
          </div>

          {/* Why This Approach */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-medium text-green-900 mb-2">Why compare total cost?</h4>
            <p className="text-green-800">
              The cheapest fuel price isn't always the best deal if you have to drive far to get it. 
              FuelHunt factors in your travel costs to show you the genuinely most cost-effective 
              options based on your location and vehicle.
            </p>
          </div>

          {/* Contact Information */}
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Mail className="w-5 h-5 text-amber-500" />
              <h3 className="text-lg font-medium text-gray-900">Contact</h3>
            </div>
            <div className="ml-7 text-gray-700 space-y-2">
              <p>Have questions, ideas for collaboration, or business development opportunities?</p>
              <div className="flex items-center space-x-2">
                <span>Get in touch:</span>
                <a 
                  href="mailto:fuelhunt@hotmail.com" 
                  className="text-amber-600 hover:text-amber-700 font-medium transition-colors"
                >
                  fuelhunt@hotmail.com
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Helping UK drivers save money on fuel
            </div>
            <button
              onClick={onClose}
              className="btn-primary"
            >
              Got it!
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutModal;